"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = void 0;
var _react = require("react");
var _propTypes = _interopRequireDefault(require("prop-types"));
var _classnames = _interopRequireDefault(require("classnames"));
var _components = require("@folio/stripes/components");
var _ResponsiveButtonGroup = _interopRequireDefault(require("../../../styles/ResponsiveButtonGroup.css"));
var _useResponsiveButtonGroupSizing = _interopRequireDefault(require("./useResponsiveButtonGroupSizing"));
var _jsxRuntime = require("react/jsx-runtime");
function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { default: obj }; }
const propTypes = {
  children: _propTypes.default.node,
  className: _propTypes.default.string,
  dropdownTriggerProps: _propTypes.default.object,
  fullWidth: _propTypes.default.bool,
  id: _propTypes.default.string,
  marginBottom0: _propTypes.default.bool,
  selectedIndex: _propTypes.default.number,
  tagName: _propTypes.default.string
};
const SIZE_OF_DOWN_ARROW_BUTTON = 46;
const calculateCumulativeThreshold = function (arrayOfNumbers, maximum) {
  let index = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : 0;
  let value = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : 0;
  // If all the numbers fit, return the last index
  if (arrayOfNumbers.reduce((partialSum, a) => partialSum + a, 0) <= maximum) {
    return (arrayOfNumbers === null || arrayOfNumbers === void 0 ? void 0 : arrayOfNumbers.length) - 1;
  }

  // Else we'll need a down arrow button, so include that in calculations
  const nextValue = value + arrayOfNumbers[index];
  if (nextValue + SIZE_OF_DOWN_ARROW_BUTTON <= maximum && index + 1 < (arrayOfNumbers === null || arrayOfNumbers === void 0 ? void 0 : arrayOfNumbers.length)) {
    return calculateCumulativeThreshold(arrayOfNumbers, maximum, index + 1, nextValue);
  }
  return index - 1;
};
const ResponsiveButtonGroup = props => {
  const {
    children: childButtons,
    // These MUST consist only of Button components
    className,
    dropdownTriggerProps = {},
    fullWidth = false,
    // Only bother with this when buttonGroupWidth < ContainerWidth
    id,
    marginBottom0,
    // Will ONLL control the margin of the dropdown button
    selectedIndex,
    tagName: Tag = 'div',
    ...rest
  } = props;
  const {
    buttons,
    cachedSizeArray,
    containerRef,
    containerWidth,
    ready
  } = (0, _useResponsiveButtonGroupSizing.default)({
    buttons: childButtons,
    id
  });
  const buttonThreshold = calculateCumulativeThreshold(cachedSizeArray, containerWidth);
  const renderActionMenuToggle = (0, _react.useCallback)(_ref => {
    var _displayButtons$lengt;
    let {
      displayButtons,
      onToggle,
      triggerRef,
      keyHandler,
      open,
      ariaProps,
      getTriggerProps
    } = _ref;
    return /*#__PURE__*/(0, _jsxRuntime.jsx)(_components.Button, {
      ref: triggerRef,
      "aria-label": "menu",
      buttonClass: (0, _classnames.default)(_ResponsiveButtonGroup.default.width100, {
        ["".concat(_ResponsiveButtonGroup.default.dropdownButtonClass)]: ((_displayButtons$lengt = displayButtons === null || displayButtons === void 0 ? void 0 : displayButtons.length) !== null && _displayButtons$lengt !== void 0 ? _displayButtons$lengt : 0) > 0
      }, {
        ["".concat(_ResponsiveButtonGroup.default.marginBottom)]: !marginBottom0
      }, {
        ["".concat(_ResponsiveButtonGroup.default.marginBottom0)]: marginBottom0
      }),
      buttonStyle: selectedIndex > buttonThreshold ? 'primary' : 'default',
      onClick: onToggle,
      onKeyDown: keyHandler,
      type: "button",
      ...getTriggerProps(),
      ...ariaProps,
      ...dropdownTriggerProps,
      children: /*#__PURE__*/(0, _jsxRuntime.jsx)(_components.Icon, {
        icon: open ? 'triangle-up' : 'triangle-down',
        iconPosition: "end"
      })
    });
  }, [buttonThreshold, dropdownTriggerProps, marginBottom0, selectedIndex]);
  const displayButtons = (0, _react.useMemo)(() => {
    var _buttons$slice;
    return buttons === null || buttons === void 0 || (_buttons$slice = buttons.slice(0, buttonThreshold + 1)) === null || _buttons$slice === void 0 ? void 0 : _buttons$slice.map((button, index) => {
      if (index === selectedIndex && !(button !== null && button !== void 0 && button.buttonStyle)) {
        return /*#__PURE__*/(0, _react.cloneElement)(button, {
          buttonStyle: 'primary'
        });
      }
      return button;
    });
  }, [buttonThreshold, buttons, selectedIndex]);
  const dropdownButtons = (0, _react.useMemo)(() => {
    var _buttons$slice2;
    return [...((_buttons$slice2 = buttons.slice(buttonThreshold + 1, buttons === null || buttons === void 0 ? void 0 : buttons.length)) === null || _buttons$slice2 === void 0 ? void 0 : _buttons$slice2.map(button => {
      return /*#__PURE__*/(0, _react.cloneElement)(button, {
        buttonStyle: 'dropdownItem'
      });
    }))];
  }, [buttonThreshold, buttons]);

  // eslint-disable-next-line react/prop-types
  const renderActionMenuContent = (0, _react.useCallback)(() => /*#__PURE__*/(0, _jsxRuntime.jsx)(_components.DropdownMenu, {
    children: [...dropdownButtons]
  }), [dropdownButtons]);
  const buttonsSlice = (0, _react.useMemo)(() => [...displayButtons, /*#__PURE__*/(0, _jsxRuntime.jsx)(_components.Dropdown, {
    className: _ResponsiveButtonGroup.default.dropdownClass,
    renderMenu: renderActionMenuContent,
    renderTrigger: triggerProps => renderActionMenuToggle({
      displayButtons,
      ...triggerProps
    })
  }, "responsiveButtonGroup-dropdown-toggle")], [displayButtons, renderActionMenuContent, renderActionMenuToggle]);
  return /*#__PURE__*/(0, _jsxRuntime.jsxs)(_jsxRuntime.Fragment, {
    children: [/*#__PURE__*/(0, _jsxRuntime.jsx)("div", {
      ref: containerRef,
      className: "width: 100%;"
    }), /*#__PURE__*/(0, _jsxRuntime.jsx)(Tag, {
      className: (0, _classnames.default)({
        ["".concat(_ResponsiveButtonGroup.default.hidden)]: !ready
      }, _ResponsiveButtonGroup.default.buttonGroup, {
        ["".concat(_ResponsiveButtonGroup.default.fullWidth)]: ready && fullWidth
      }, className),
      style: fullWidth ? {
        width: "".concat(containerWidth, "px")
      } : {},
      ...rest,
      children: !ready || buttonThreshold === (cachedSizeArray === null || cachedSizeArray === void 0 ? void 0 : cachedSizeArray.length) - 1 ? buttons === null || buttons === void 0 ? void 0 : buttons.map((button, index) => {
        if (index === selectedIndex && !(button !== null && button !== void 0 && button.buttonStyle)) {
          return /*#__PURE__*/(0, _react.cloneElement)(button, {
            buttonStyle: 'primary'
          });
        }
        return button;
      }) : buttonsSlice
    })]
  });
};
ResponsiveButtonGroup.propTypes = propTypes;
var _default = exports.default = ResponsiveButtonGroup;